import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { importJobsApi } from '@/lib/api';

interface ImportJob {
  id: string;
  hogarId: string;
  miembroId: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  status: string;
  dedupeMode: string;
  totalRows: number;
  processedRows: number;
  importedRows: number;
  duplicateRows: number;
  errorRows: number;
  progress: number;
  errors: unknown[];
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  hogar?: { id: string; nombre: string };
  miembro?: { id: string; nombre: string; email: string };
}

interface Stats {
  total: number;
  last24h: number;
  byStatus: Record<string, number>;
  successRate: number;
}

const statusColors: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-800',
  PROCESSING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-yellow-100 text-yellow-800',
};

const ITEMS_PER_PAGE = 20;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDuration(start: string, end: string | null): string {
  if (!end) return 'En progreso';
  const duration = new Date(end).getTime() - new Date(start).getTime();
  const seconds = Math.floor(duration / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

export default function ImportJobs() {
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    fromDate: '',
    toDate: '',
  });
  const [page, setPage] = useState(0);
  const [selectedJob, setSelectedJob] = useState<ImportJob | null>(null);

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['importJobsStats'],
    queryFn: importJobsApi.getStats,
  });

  // Fetch jobs
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['importJobs', filters, page],
    queryFn: () =>
      importJobsApi.getAll({
        ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
        limit: ITEMS_PER_PAGE,
        offset: page * ITEMS_PER_PAGE,
      }),
  });

  const stats: Stats = statsData?.data || { total: 0, last24h: 0, byStatus: {}, successRate: 0 };
  const jobs: ImportJob[] = jobsData?.data?.jobs || [];
  const total = jobsData?.data?.total || 0;
  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Import Jobs</h1>
        <p className="text-gray-600">Monitoreo de trabajos de importacion CSV/Excel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="card p-4">
          <div className="text-sm text-gray-500">Total Jobs</div>
          <div className="text-2xl font-bold text-gray-900">
            {statsLoading ? '--' : stats.total.toLocaleString()}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Ultimas 24h</div>
          <div className="text-2xl font-bold text-blue-600">
            {statsLoading ? '--' : stats.last24h}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Completados</div>
          <div className="text-2xl font-bold text-green-600">
            {statsLoading ? '--' : stats.byStatus['COMPLETED'] || 0}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Fallidos</div>
          <div className="text-2xl font-bold text-red-600">
            {statsLoading ? '--' : stats.byStatus['FAILED'] || 0}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-sm text-gray-500">Tasa de Exito</div>
          <div className="text-2xl font-bold text-purple-600">
            {statsLoading ? '--' : `${stats.successRate}%`}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4 mb-4">
        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input text-sm"
            >
              <option value="">Todos</option>
              <option value="PENDING">Pendiente</option>
              <option value="PROCESSING">Procesando</option>
              <option value="COMPLETED">Completado</option>
              <option value="FAILED">Fallido</option>
              <option value="CANCELLED">Cancelado</option>
            </select>
          </div>

          <div className="flex-1 max-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="input text-sm"
            />
          </div>

          <div className="flex-1 max-w-[180px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="input text-sm"
            />
          </div>

          <button
            onClick={() => {
              setFilters({ status: '', fromDate: '', toDate: '' });
              setPage(0);
            }}
            className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2"
          >
            Limpiar
          </button>
        </div>
      </div>

      {/* Error */}
      {jobsError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          Error al cargar jobs: {(jobsError as Error).message}
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {jobsLoading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : jobs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No se encontraron jobs de importacion</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Fecha
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Archivo
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Hogar
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Estado
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Progreso
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Resultados
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm">
                        <div className="font-medium text-gray-900">
                          {new Date(job.createdAt).toLocaleDateString('es-MX')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(job.createdAt).toLocaleTimeString('es-MX')}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={job.filename}>
                          {job.filename}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatBytes(job.fileSize)} | {job.totalRows} filas
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-gray-900">{job.hogar?.nombre || '-'}</div>
                        <div className="text-xs text-gray-500">{job.miembro?.nombre || '-'}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[job.status] || 'bg-gray-100 text-gray-800'}`}>
                          {job.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {job.status === 'PROCESSING' ? (
                          <div className="w-24">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500 transition-all"
                                style={{ width: `${job.progress}%` }}
                              />
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{job.progress}%</div>
                          </div>
                        ) : job.status === 'COMPLETED' || job.status === 'FAILED' ? (
                          <div className="text-xs text-gray-500">
                            {job.startedAt && formatDuration(job.startedAt, job.completedAt)}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {job.status === 'COMPLETED' || job.status === 'FAILED' ? (
                          <div className="space-y-0.5">
                            <div className="text-green-600">{job.importedRows} importadas</div>
                            {job.duplicateRows > 0 && (
                              <div className="text-yellow-600">{job.duplicateRows} duplicadas</div>
                            )}
                            {job.errorRows > 0 && (
                              <div className="text-red-600">{job.errorRows} errores</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => setSelectedJob(job)}
                          className="text-sm text-primary-600 hover:text-primary-700"
                        >
                          Ver Detalles
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
              <div className="text-sm text-gray-500">
                Mostrando {page * ITEMS_PER_PAGE + 1} - {Math.min((page + 1) * ITEMS_PER_PAGE, total)} de {total}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <span className="px-3 py-1 text-sm">
                  {page + 1} / {totalPages || 1}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">Detalles del Job</h2>
                <p className="text-sm text-gray-500">{selectedJob.id}</p>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Archivo</div>
                  <div className="font-medium">{selectedJob.filename}</div>
                  <div className="text-sm text-gray-500">{formatBytes(selectedJob.fileSize)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Estado</div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[selectedJob.status]}`}>
                    {selectedJob.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Hogar</div>
                  <div className="font-medium">{selectedJob.hogar?.nombre || '-'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Usuario</div>
                  <div className="font-medium">{selectedJob.miembro?.nombre || '-'}</div>
                  <div className="text-sm text-gray-500">{selectedJob.miembro?.email}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Modo Dedupe</div>
                  <div className="font-medium">{selectedJob.dedupeMode}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Creado</div>
                  <div className="font-medium">
                    {new Date(selectedJob.createdAt).toLocaleString('es-MX')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Duracion</div>
                  <div className="font-medium">
                    {selectedJob.startedAt
                      ? formatDuration(selectedJob.startedAt, selectedJob.completedAt)
                      : '-'}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="text-sm font-medium mb-2">Resultados</div>
                <div className="grid grid-cols-5 gap-2 text-center">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold">{selectedJob.totalRows}</div>
                    <div className="text-xs text-gray-500">Total</div>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">{selectedJob.processedRows}</div>
                    <div className="text-xs text-gray-500">Procesadas</div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">{selectedJob.importedRows}</div>
                    <div className="text-xs text-gray-500">Importadas</div>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <div className="text-lg font-bold text-yellow-600">{selectedJob.duplicateRows}</div>
                    <div className="text-xs text-gray-500">Duplicadas</div>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <div className="text-lg font-bold text-red-600">{selectedJob.errorRows}</div>
                    <div className="text-xs text-gray-500">Errores</div>
                  </div>
                </div>
              </div>

              {Array.isArray(selectedJob.errors) && selectedJob.errors.length > 0 && (
                <div className="border-t pt-4">
                  <div className="text-sm font-medium mb-2">Errores ({selectedJob.errors.length})</div>
                  <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
                    {selectedJob.errors.slice(0, 10).map((err: unknown, idx: number) => (
                      <div key={idx} className="text-sm text-red-600 mb-1">
                        Fila {(err as { row?: number }).row}: {(err as { error?: string }).error}
                      </div>
                    ))}
                    {selectedJob.errors.length > 10 && (
                      <div className="text-sm text-gray-500">
                        ... y {selectedJob.errors.length - 10} errores mas
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button onClick={() => setSelectedJob(null)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
